<script setup>
import { computed, ref, watch } from 'vue'
import { marked, Marked } from 'marked'
import DOMPurify from 'dompurify'
import { formatTime } from '@/utils/datetime'
import { API, HOST } from '@/utils/http'
import { useUserStore } from '@/stores/user'
import { useGeoStore } from '@/stores/geo'
import { useRoute, useRouter } from 'vue-router'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import FeaturesList from '@/components/features/FeaturesList.vue'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Download } from 'lucide-vue-next'
import ReportSettingsDialog from './ReportSettingsDialog.vue'
import ReportExportDialog from './ReportExportDialog.vue'

// Configure marked options
marked.setOptions({
  breaks: true,
  gfm: true,
  smartLists: true,
  smartypants: true
})

// Isolated marked instance: render `feature://<code>` links (marked v18 token form)
// as non-navigating anchors that open the feature's popup on the map. Kept separate
// from the global `marked` so other views (e.g. chat) are unaffected.
const reportMarked = new Marked({ breaks: true, gfm: true })
reportMarked.use({
  renderer: {
    link({ href, text }) {
      const match = /^feature:\/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.exec(href || '')
      if (match) {
        return `<a data-feature-code="${match[1]}" class="feature-link cursor-pointer text-primary underline" role="button" tabindex="0">${text}</a>`
      }
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`
    }
  }
})

const userStore = useUserStore()

const geoStore = useGeoStore()
const route = useRoute()
const router = useRouter()
const settings = computed(() => userStore.settings || { tz: 'UTC' })

const props = defineProps({
  report: {
    type: Object,
    required: true
  },
  organizationId: {
    type: Number,
    default: null
  }
})

const emit = defineEmits(['report-updated'])

const contentRef = ref(null)
const exportDialog = ref(null)

const currentMember = computed(() => userStore.member)
const isOwner = computed(() => {
  return currentMember.value?.member_id === props.report?.member_id
})

const handleReportUpdated = (updatedReport) => {
  emit('report-updated', updatedReport)
}

const renderedContent = computed(() => {
  if (!props.report?.content) return ''
  const htmlContent = reportMarked.parse(props.report.content)
  return DOMPurify.sanitize(htmlContent, {
    ADD_ATTR: ['data-feature-code', 'role', 'tabindex', 'target']
  })
})

// Open feature links in the report on the map popup instead of navigating.
const openFeatureLink = (link) => {
  geoStore.openFeaturePopupByCode(link.getAttribute('data-feature-code'))
}

const handleContentClick = (event) => {
  if (!(event.target instanceof Element)) return
  const link = event.target.closest('a.feature-link[data-feature-code]')
  if (!link) return
  event.preventDefault()
  openFeatureLink(link)
}

// The feature links carry role="button" tabindex="0", so keyboard users expect
// Enter/Space to activate them just like a click.
const handleContentKeydown = (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return
  if (!(event.target instanceof Element)) return
  const link = event.target.closest('a.feature-link[data-feature-code]')
  if (!link) return
  event.preventDefault()
  openFeatureLink(link)
}

// Data tab: the features the report was generated from, fetched by code.
const activeTab = ref('report')
const affectedFeatures = ref([])
const loadingFeatures = ref(false)

// Codes the review was generated from. Prefer the stored full input set; fall back
// to parsing feature:// links from the content for legacy reports saved before
// feature_codes existed.
const featureCodes = computed(() => {
  const stored = props.report?.feature_codes
  if (stored && stored.length) return stored
  const content = props.report?.content || ''
  const codes = new Set()
  const re = /feature:\/\/([0-9a-fA-F-]{8,})/g
  let match
  while ((match = re.exec(content)) !== null) codes.add(match[1])
  return [...codes]
})
const hasAffectedData = computed(() => featureCodes.value.length > 0)

const loadAffectedFeatures = async () => {
  if (!featureCodes.value.length) {
    affectedFeatures.value = []
    return
  }
  loadingFeatures.value = true
  try {
    const results = await Promise.all(
      featureCodes.value.map((code) =>
        API({ method: 'GET', url: `${HOST}/geo/feature/${code}` })
          .then((r) => r.data)
          .catch(() => null)
      )
    )
    affectedFeatures.value = results.filter(Boolean)
  } finally {
    loadingFeatures.value = false
  }
}

watch(
  () => props.report?.report_uuid,
  () => {
    activeTab.value = 'report'
    affectedFeatures.value = []
  }
)

watch(featureCodes, (codes) => {
  if (codes.length && activeTab.value === 'data' && !affectedFeatures.value.length) {
    loadAffectedFeatures()
  }
})

watch(activeTab, (tab) => {
  if (tab === 'data' && hasAffectedData.value && !affectedFeatures.value.length) {
    loadAffectedFeatures()
  }
})

const formattedDate = computed(() => {
  return formatTime(props.report.created_at, settings.value.tz, 'MMMM D, YYYY [at] HH:mm')
})

// Version selector
const availableVersions = computed(() => {
  return props.report?.available_versions || []
})

const hasMultipleVersions = computed(() => {
  return availableVersions.value.length > 1
})

const currentVersion = computed(() => {
  // Get version from route query or use the report's current version
  const queryVersion = route.query.version
  if (queryVersion) {
    return parseInt(queryVersion, 10)
  }
  return props.report?.version
})

const selectedVersion = computed({
  get: () => currentVersion.value?.toString() || '',
  set: (value) => {
    const version = parseInt(value, 10)
    const latestVersion = availableVersions.value[0]

    // Update route with version query param
    if (version === latestVersion) {
      // Remove version param for latest version
      const query = { ...route.query }
      delete query.version
      router.push({ query })
    } else {
      router.push({ query: { ...route.query, version } })
    }
  }
})
</script>

<template>
  <div class="h-full flex flex-col overflow-hidden bg-background">
    <!-- Header -->
    <div class="px-4 py-3 border-b border-border flex-shrink-0">
      <div class="flex items-start justify-between">
        <div class="flex-1 min-w-0">
          <h1 class="text-lg font-semibold truncate">{{ report.title || 'Untitled Report' }}</h1>
          <div class="flex items-center gap-1.5 mt-1">
            <span class="text-xs text-muted-foreground">{{ formattedDate }}</span>
            <Badge v-if="report.is_public" variant="default" class="text-xs px-1.5 py-0 h-4">
              Public
            </Badge>
            <Button
              v-if="organizationId"
              variant="ghost"
              size="icon"
              class="h-7 w-7"
              aria-label="Export report"
              @click="exportDialog?.open()"
            >
              <Download class="h-4 w-4" />
            </Button>
            <ReportExportDialog
              v-if="organizationId"
              ref="exportDialog"
              :report="report"
              :organization-id="organizationId"
            />
            <ReportSettingsDialog
              v-if="isOwner && organizationId"
              :report="report"
              :organization-id="organizationId"
              @report-updated="handleReportUpdated"
            />
          </div>
        </div>

        <!-- Version selector -->
        <div v-if="hasMultipleVersions" class="ml-3 flex-shrink-0">
          <Select v-model="selectedVersion">
            <SelectTrigger class="w-[120px]">
              <SelectValue>
                Version {{ currentVersion }}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem
                  v-for="v in availableVersions"
                  :key="v"
                  :value="v.toString()"
                >
                  <span>
                    Version {{ v }}
                    <span v-if="v === availableVersions[0]" class="text-muted-foreground ml-1">(latest)</span>
                  </span>
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>

    <!-- Tabs: Report (markdown) / Data (affected features feed) -->
    <Tabs v-model="activeTab" class="flex-1 flex flex-col overflow-hidden">
      <TabsList class="mx-4 mt-2 w-fit">
        <TabsTrigger value="report">Report</TabsTrigger>
        <TabsTrigger value="data">Data</TabsTrigger>
      </TabsList>

      <TabsContent value="report" class="flex-1 overflow-y-auto p-4 relative mt-0">
        <div ref="contentRef" class="relative">
          <div v-if="report.content" class="prose prose-sm dark:prose-invert max-w-none break-words">
            <div v-html="renderedContent" class="break-words" @click="handleContentClick" @keydown="handleContentKeydown"></div>
          </div>
          <div v-else class="text-center text-muted-foreground py-8">
            <p>This report has no content.</p>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="data" class="flex-1 overflow-y-auto p-4 mt-0">
        <div v-if="loadingFeatures" class="text-center text-muted-foreground py-8">
          <p>Loading data…</p>
        </div>
        <FeaturesList v-else-if="affectedFeatures.length" :features="affectedFeatures" />
        <div v-else class="text-center text-muted-foreground py-8">
          <p>No associated data.</p>
        </div>
      </TabsContent>
    </Tabs>
  </div>
</template>
